// ViewCompletedOrders.jsx
import React, { useState, useEffect } from 'react';
import api from '../Service/api'; // your axios instance
import './ViewCompleteOrders.css';

const ViewCompletedOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const formatDate = (date) => date.toISOString().split('T')[0];
    const defaultStart = formatDate(thirtyDaysAgo);
    const defaultEnd = formatDate(today);

    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    fetchCompletedOrders(defaultStart, defaultEnd);
  }, []);

  useEffect(() => {
    filterOrders();
  }, [searchTerm, searchBy, orders]);

  const fetchCompletedOrders = async (start = '', end = '') => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;

      const response = await api.get('/api/orders/completed', { params });
      setOrders(response.data || []);
      setFilteredOrders(response.data || []);
      setError('');
    } catch (error) {
      setError('Failed to fetch completed orders');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    fetchCompletedOrders(startDate, endDate);
  };

  const resetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchCompletedOrders();
  };

  const filterOrders = () => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = orders.filter(order => {
      if (searchBy === 'company') {
        return order.companyName?.toLowerCase().includes(term);
      } else if (searchBy === 'product') {
        return order.productName?.toLowerCase().includes(term);
      } else if (searchBy === 'customer') {
        return order.customerName?.toLowerCase().includes(term);
      } else {
        return (
          order.companyName?.toLowerCase().includes(term) ||
          order.productName?.toLowerCase().includes(term) ||
          order.customerName?.toLowerCase().includes(term)
        );
      }
    });
    setFilteredOrders(filtered);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSearchByChange = (e) => setSearchBy(e.target.value);
  const clearSearch = () => {
    setSearchTerm('');
    setSearchBy('all');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

const handlePrint = () => {
  try {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Pop-up blocked. Please allow pop-ups for this site.');
      return;
    }

    const table = document.querySelector('.completed-table');
    if (!table) {
      alert('No table found to print.');
      printWindow.close();
      return;
    }

    const rowCount = table.querySelectorAll('tbody tr').length;
    const tableContent = rowCount === 0
      ? '<p>No completed orders to display.</p>'
      : table.cloneNode(true).outerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Completed Orders</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; background: white; }
            h2 { color: #333; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .completed-highlight { background: #fef9c3; }
            p { font-style: italic; color: #666; }
          </style>
        </head>
        <body>
          <h2>Completed Orders</h2>
          ${tableContent}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    printWindow.onload = () => {
      printWindow.print();
      // Fallback: close after a short delay (some browsers don't fire onafterprint)
      setTimeout(() => {
        if (!printWindow.closed) printWindow.close();
      }, 1000);
    };

    printWindow.onafterprint = () => {
      printWindow.close();
    };
  } catch (error) {
    console.error('Print error:', error);
    alert('Printing failed. Please try again.');
  }
};

  if (loading) {
    return <div className="completed-loading">Loading completed orders...</div>;
  }

  return (
    <div className="completed-container">
      <h2 className="completed-title">Completed Orders</h2>

      {/* Date Filter Section with Print Button */}
      <div className="date-filter-section">
        <div className="date-input-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="date-input-group">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button onClick={handleDateFilter} className="apply-date-btn">
          Apply Date Filter
        </button>
        <button onClick={resetDateFilter} className="reset-date-btn">
          Reset
        </button>
        <button onClick={handlePrint} className="print-btn">
          Print
        </button>
      </div>

      {/* Search Bar Section */}
      <div className="completed-search-section">
        <div className="completed-search-wrapper">
          <div className="completed-search-input-wrapper">
            <input
              type="text"
              className="completed-search-input"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <select
            className="completed-search-select"
            value={searchBy}
            onChange={handleSearchByChange}
          >
            <option value="all">Search All Fields</option>
            <option value="company">Company Name</option>
            <option value="product">Product Name</option>
            <option value="customer">Customer Name</option>
          </select>
        </div>

        <div className="completed-search-stats">
          Found <strong>{filteredOrders.length}</strong>{' '}
          {filteredOrders.length === 1 ? 'order' : 'orders'}
          {searchTerm && (
            <span>
              {' '}
              for "<strong>{searchTerm}</strong>"
            </span>
          )}
        </div>
      </div>

      {error && <div className="completed-error">{error}</div>}

      {filteredOrders.length === 0 ? (
        <div className="completed-empty">
          {orders.length === 0
            ? 'No completed orders found'
            : 'No orders match your search criteria'}
        </div>
      ) : (
        <div className="completed-table-wrapper">
          <table className="completed-table">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Product Name</th>
                <th>Quantity</th>
                <th>Customer Name</th>
                <th>Customer Requirement</th>
                <th>Completed On</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    {searchTerm &&
                    searchBy === 'company' &&
                    order.companyName
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ? (
                      <mark className="completed-highlight">
                        {order.companyName}
                      </mark>
                    ) : (
                      order.companyName
                    )}
                  </td>
                  <td>
                    {searchTerm &&
                    searchBy === 'product' &&
                    order.productName
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ? (
                      <mark className="completed-highlight">
                        {order.productName}
                      </mark>
                    ) : (
                      order.productName
                    )}
                  </td>
                  <td>{order.quantity}</td>
                  <td>
                    {searchTerm &&
                    searchBy === 'customer' &&
                    order.customerName
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ? (
                      <mark className="completed-highlight">
                        {order.customerName}
                      </mark>
                    ) : (
                      order.customerName
                    )}
                  </td>
                  <td>{order.requirement}</td>
                  <td>{formatDate(order.time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ViewCompletedOrders;